<!-- Loading -->
<div *ngIf="isPageLoading" class="d-flex justify-content-center align-items-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
</div>

<ng-container *ngIf="!isPageLoading">

  <!-- ── Page Header ─────────────────────────────────────────────── -->
  <div class="detail-header mb-4">
    <div class="d-flex align-items-start gap-3">

      <!-- Channel icon -->
      <div class="channel-icon-badge" [style.background-color]="channelColor + '1a'" [style.color]="channelColor">
        <i class="{{ channelIcon }} fs-4"></i>
      </div>

      <!-- Title + description with inline edit -->
      <div class="flex-grow-1">
        <form [formGroup]="alertServiceForm">
          <ng-container *ngIf="editingSection !== 'title'">
            <div class="d-flex align-items-center gap-2 mb-1">
              <h4 class="mb-0 fw-bold">{{ alertServiceForm.get('title')?.value }}</h4>
              <button class="btn btn-sm btn-link p-0 text-muted" (click)="startEditing('title')" title="Edit title">
                <i class="bi bi-pencil fs-6"></i>
              </button>
            </div>
            <p class="text-muted mb-0 small">{{ alertServiceForm.get('sDesc')?.value || 'No description' }}</p>
          </ng-container>
          <ng-container *ngIf="editingSection === 'title'">
            <div class="d-flex align-items-center gap-2 mb-2">
              <input type="text" class="form-control form-control-sm" style="width: 320px;" formControlName="title" placeholder="Title">
              <button class="btn btn-sm btn-primary" (click)="updateAlertsServiceMaster()" [disabled]="isLoadingServiceUpdate">
                <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoadingServiceUpdate"></span>Save
              </button>
              <button class="btn btn-sm btn-outline-secondary" (click)="stopEditing()">Cancel</button>
            </div>
            <input type="text" class="form-control form-control-sm" style="width: 480px;" formControlName="sDesc" placeholder="Description">
          </ng-container>
        </form>
      </div>

      <!-- Status -->
      <span class="badge rounded-pill px-3 py-2 fs-6 ms-auto"
        [ngClass]="alertServiceForm.value.status === 'Active' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
        <i class="bi bi-circle-fill me-1" style="font-size: 7px; vertical-align: middle;"></i>
        {{ alertServiceForm.value.status }}
      </span>
    </div>

    <!-- Meta row -->
    <div class="d-flex flex-wrap gap-4 mt-3 pt-3 border-top text-muted small">
      <span><i class="bi bi-lightning-fill me-1" [style.color]="channelColor"></i>{{ alertServiceForm.get('alertType')?.value }}</span>
      <span><i class="bi bi-clock me-1"></i>Last Run: <strong class="text-dark">{{ alertServiceForm.get('lastExecutedOn')?.value || 'Never' }}</strong></span>
      <span><i class="bi bi-calendar-check me-1"></i>Next Run: <strong class="text-dark">{{ alertServiceForm.get('nextExecutionTime')?.value || '--' }}</strong></span>
    </div>
  </div>

  <!-- ── Two-column layout ──────────────────────────────────────── -->
  <div class="d-flex gap-4 align-items-start">

    <!-- Left sidebar nav -->
    <div class="detail-sidebar">
      <div class="detail-nav-item" [class.active]="activeSection === 'overview'" (click)="activeSection = 'overview'">
        <i class="bi bi-info-circle detail-nav-icon"></i><span>Overview</span>
      </div>
      <div class="detail-nav-item" *ngIf="channelConfigRequired" [class.active]="activeSection === 'channel'" (click)="activeSection = 'channel'">
        <i class="bi bi-send detail-nav-icon"></i><span>Channel Config</span>
      </div>
      <div class="detail-nav-item" [class.active]="activeSection === 'datasource'" (click)="activeSection = 'datasource'">
        <i class="bi bi-database detail-nav-icon"></i><span>Data Source</span>
      </div>
      <div class="detail-nav-item" [class.active]="activeSection === 'scheduler'" (click)="activeSection = 'scheduler'; stopEditing()">
        <i class="bi bi-calendar3 detail-nav-icon"></i><span>Scheduler</span>
      </div>
      <div class="detail-nav-item" [class.active]="activeSection === 'variables'" (click)="activeSection = 'variables'; stopEditing()">
        <i class="bi bi-braces detail-nav-icon"></i><span>Variables</span>
      </div>
      <div class="detail-nav-item" [class.active]="activeSection === 'advanced'" (click)="activeSection = 'advanced'">
        <i class="bi bi-gear detail-nav-icon"></i><span>Advanced</span>
      </div>
    </div>

    <!-- ── Content panel ──────────────────────────────────────────── -->
    <div class="flex-grow-1" style="min-width: 0;">

      <!-- ── OVERVIEW ─────────────────────────────────────────────── -->
      <div *ngIf="activeSection === 'overview'" class="section-card">
        <div class="section-card-header">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-info-circle text-primary fs-5"></i>
            <span class="fw-semibold">Overview</span>
          </div>
        </div>
        <div class="section-card-body">
          <div class="row g-4">
            <div class="col-md-6">
              <div class="detail-field-label">Title</div>
              <div class="detail-field-value">{{ alertServiceForm.get('title')?.value }}</div>
            </div>
            <div class="col-md-6">
              <div class="detail-field-label">Channel</div>
              <div class="detail-field-value">
                <i class="{{ channelIcon }} me-1" [style.color]="channelColor"></i>
                {{ alertServiceForm.get('alertType')?.value }}
              </div>
            </div>
            <div class="col-12">
              <div class="detail-field-label">Description</div>
              <div class="detail-field-value">{{ alertServiceForm.get('sDesc')?.value || '—' }}</div>
            </div>
            <div class="col-md-4">
              <div class="detail-field-label">Status</div>
              <div class="detail-field-value">
                <span class="badge rounded-pill"
                  [ngClass]="alertServiceForm.value.status === 'Active' ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'">
                  {{ alertServiceForm.value.status }}
                </span>
              </div>
            </div>
            <div class="col-md-4">
              <div class="detail-field-label">Last Run</div>
              <div class="detail-field-value">{{ alertServiceForm.get('lastExecutedOn')?.value || 'Never' }}</div>
            </div>
            <div class="col-md-4">
              <div class="detail-field-label">Next Run</div>
              <div class="detail-field-value">{{ alertServiceForm.get('nextExecutionTime')?.value || '—' }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- ── CHANNEL CONFIG ────────────────────────────────────────── -->
      <div *ngIf="activeSection === 'channel'" class="section-card">
        <div class="section-card-header">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-send text-primary fs-5"></i>
            <span class="fw-semibold">Channel Configuration</span>
          </div>
          <ng-container *ngIf="editingSection !== 'email'">
            <button class="btn btn-sm btn-outline-secondary" (click)="toggleEmailEdit()"
              [disabled]="isUniversalEditing">
              <i class="bi bi-pencil me-1"></i>Edit
            </button>
          </ng-container>
          <div class="d-flex gap-2" *ngIf="editingSection === 'email'">
            <button class="btn btn-sm btn-outline-secondary" (click)="stopEditing()">Cancel</button>
            <button class="btn btn-sm btn-primary" (click)="updateAlertsServiceMaster()" [disabled]="isLoadingServiceUpdate">
              <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoadingServiceUpdate"></span>Save
            </button>
          </div>
        </div>
        <div class="section-card-body">
          <form [formGroup]="alertServiceForm">

            <!-- EMAIL -->
            <ng-container *ngIf="alertServiceForm.get('alertType')?.value === 'Email'">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label fw-semibold">To <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" formControlName="emailTo" placeholder="recipient@example.com">
                </div>
                <div class="col-md-6">
                  <label class="form-label fw-semibold">Send Test Email</label>
                  <div class="input-group input-group-sm">
                    <input type="text" class="form-control" formControlName="testEmailReceiver" placeholder="test@example.com">
                    <button class="btn btn-outline-primary" type="button" (click)="sendTestEmail()" [disabled]="isLoadingTestEmail">
                      <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoadingTestEmail"></span>Send
                    </button>
                  </div>
                  <app-form-errors [control]="alertServiceForm.get('testEmailReceiver')" fieldName="Receiver's Email"></app-form-errors>
                </div>
                <div class="col-md-6">
                  <label class="form-label">CC</label>
                  <input type="text" class="form-control form-control-sm" formControlName="ccTo" placeholder="cc@example.com">
                </div>
                <div class="col-md-6">
                  <label class="form-label">BCC</label>
                  <input type="text" class="form-control form-control-sm" formControlName="bccTo" placeholder="bcc@example.com">
                </div>
                <div class="col-12">
                  <label class="form-label fw-semibold">Subject</label>
                  <input type="text" class="form-control form-control-sm" formControlName="aSubject" placeholder="Subject line">
                </div>
                <div class="col-12">
                  <label class="form-label fw-semibold">Body <span class="text-danger">*</span></label>
                  <ng-container *ngIf="isUniversalEditing && editingSection === 'email'; else emailBodyPreview">
                    <app-rich-text-editor [control]="alertServiceForm.controls.aBody"></app-rich-text-editor>
                  </ng-container>
                  <ng-template #emailBodyPreview>
                    <div class="body-preview border rounded p-3"
                      [innerHTML]="sanitizer.bypassSecurityTrustHtml(alertServiceForm.controls.aBody.value!)">
                    </div>
                  </ng-template>
                </div>
              </div>
            </ng-container>

            <!-- WHATSAPP -->
            <ng-container *ngIf="alertServiceForm.get('alertType')?.value === 'WhatsApp'">
              <div class="col-12">
                <label class="form-label fw-semibold">Message Body <span class="text-danger">*</span></label>
                <ng-container *ngIf="isUniversalEditing && editingSection === 'email'; else waBodyPreview">
                  <app-rich-text-editor [control]="alertServiceForm.controls.aBody"></app-rich-text-editor>
                </ng-container>
                <ng-template #waBodyPreview>
                  <div class="body-preview border rounded p-3"
                    [innerHTML]="sanitizer.bypassSecurityTrustHtml(alertServiceForm.controls.aBody.value!)">
                  </div>
                </ng-template>
              </div>
            </ng-container>

            <!-- SMS -->
            <ng-container *ngIf="alertServiceForm.get('alertType')?.value === 'SMS'">
              <div class="col-12">
                <label class="form-label fw-semibold">Message Body</label>
                <textarea class="form-control form-control-sm" formControlName="aBody" rows="5"></textarea>
              </div>
            </ng-container>

          </form>
        </div>
      </div>

      <!-- ── DATA SOURCE ───────────────────────────────────────────── -->
      <div *ngIf="activeSection === 'datasource'" class="section-card">
        <div class="section-card-header">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-database text-primary fs-5"></i>
            <span class="fw-semibold">Data Source</span>
          </div>
          <ng-container *ngIf="editingSection !== 'serviceDetails'">
            <button class="btn btn-sm btn-outline-secondary" (click)="toggleServiceDetailsEdit()"
              [disabled]="isUniversalEditing">
              <i class="bi bi-pencil me-1"></i>Edit
            </button>
          </ng-container>
          <div class="d-flex gap-2" *ngIf="editingSection === 'serviceDetails'">
            <button class="btn btn-sm btn-outline-secondary" (click)="stopEditing()">Cancel</button>
            <button class="btn btn-sm btn-primary" (click)="updateAlertsServiceMaster()" [disabled]="isLoadingServiceUpdate">
              <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoadingServiceUpdate"></span>Save
            </button>
          </div>
        </div>
        <div class="section-card-body">
          <form [formGroup]="alertServiceForm">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Database Connection <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" formControlName="dbConnid">
                  <option value="0" disabled>-- Select Connection --</option>
                  <option *ngFor="let conn of dbConnectionList" [value]="conn.dbConnId">{{ conn.dbName }}</option>
                </select>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Data Source Type <span class="text-danger">*</span></label>
                <app-valuelist-selector [control]="alertServiceForm.controls.dataSourceType"
                  [vlCode]="'ANNDST'" [placeholder]="'-- Select Type --'">
                </app-valuelist-selector>
              </div>
              <div class="col-12">
                <label class="form-label fw-semibold">Data Source Definition</label>
                <div class="text-muted small mb-1">
                  Expected column aliases: <code>NTo</code>, <code>NSubject</code>, <code>NContent</code>,
                  <code>MasterIdCol</code>, <code>MasterTypeCol</code>, <code>ActionTypeCol</code>
                </div>
                <textarea class="form-control form-control-sm font-monospace" formControlName="dataSourceDef"
                  rows="12" style="resize: vertical;"
                  placeholder="SELECT ... FROM ... WHERE NOT EXISTS (...)"></textarea>
              </div>
            </div>
          </form>
        </div>
      </div>

      <!-- ── SCHEDULER ─────────────────────────────────────────────── -->
      <div *ngIf="activeSection === 'scheduler'" class="section-card">
        <div class="section-card-header">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-calendar3 text-primary fs-5"></i>
            <span class="fw-semibold">Scheduler</span>
          </div>
          <button class="btn btn-sm btn-outline-primary" (click)="onAddSchedular()">
            <i class="bi bi-plus-circle me-1"></i>New Scheduler
          </button>
        </div>
        <div class="section-card-body">

          <!-- Create new scheduler form -->
          <div *ngIf="showAlertSchedularForm" class="mb-4 p-3 border rounded bg-light">
            <h6 class="fw-semibold mb-3">Create New Scheduler</h6>
            <form [formGroup]="alertSchedularForm">
              <div class="row g-3">
                <div class="col-md-6">
                  <label class="form-label">Name <span class="text-danger">*</span></label>
                  <input type="text" class="form-control form-control-sm" [formControl]="alertSchedularForm.controls.aName" placeholder="Name">
                  <app-form-errors [control]="alertSchedularForm.controls.aName"></app-form-errors>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Code</label>
                  <input type="text" class="form-control form-control-sm" [formControl]="alertSchedularForm.controls.aCode" placeholder="Code">
                </div>
                <div class="col-md-6">
                  <label class="form-label">Frequency (minutes) <span class="text-danger">*</span></label>
                  <input type="number" class="form-control form-control-sm" [formControl]="alertSchedularForm.controls.aFreq">
                  <app-form-errors [control]="alertSchedularForm.controls.aFreq"></app-form-errors>
                </div>
                <div class="col-md-6">
                  <label class="form-label">Scheduler Type <span class="text-danger">*</span></label>
                  <app-valuelist-selector [control]="alertSchedularForm.controls.sType" [vlCode]="'ANNST'" [placeholder]="'Scheduler Type'"></app-valuelist-selector>
                  <app-form-errors [control]="alertSchedularForm.controls.sType"></app-form-errors>
                </div>
                <div class="col-12">
                  <label class="form-label">Description</label>
                  <textarea class="form-control form-control-sm" [formControl]="alertSchedularForm.controls.aDesc" rows="2"></textarea>
                </div>
              </div>
            </form>
            <div class="d-flex justify-content-end gap-2 mt-3">
              <button class="btn btn-sm btn-outline-secondary" (click)="cancelAlertSchedularForm()">Cancel</button>
              <button class="btn btn-sm btn-primary" (click)="submitAlertSchedularForm()" [disabled]="isLoading">
                <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoading"></span>Save Scheduler
              </button>
            </div>
          </div>

          <!-- Assign scheduler to service -->
          <form [formGroup]="alertsServiceSchedularForm">
            <div class="row g-3">
              <div class="col-md-6">
                <label class="form-label fw-semibold">Select Scheduler <span class="text-danger">*</span></label>
                <select class="form-select form-select-sm" formControlName="schedularId">
                  <option [ngValue]="0" disabled>-- Select Scheduler --</option>
                  <option *ngFor="let item of schedularDropDownList" [ngValue]="item.schedularId">
                    {{ item.iName }} ({{ item.schedularType }}) — {{ item.frequencyInMinutes }} mins
                  </option>
                </select>
                <small class="text-info d-block mt-1" *ngIf="alertsServiceSchedularForm.controls.schedularId.value">
                  {{ getSchedularData(alertsServiceSchedularForm.controls.schedularId.value)?.iDesc }}
                </small>
              </div>
              <div class="col-md-3">
                <label class="form-label">Name</label>
                <input type="text" class="form-control form-control-sm"
                  [value]="getSchedularData(alertsServiceSchedularForm.controls.schedularId.value)?.iName || ''"
                  readonly>
              </div>
              <div class="col-md-3">
                <label class="form-label">Frequency (mins)</label>
                <input type="text" class="form-control form-control-sm"
                  [value]="getSchedularData(alertsServiceSchedularForm.controls.schedularId.value)?.frequencyInMinutes || ''"
                  readonly>
              </div>

              <div class="col-md-6">
                <label class="form-label fw-semibold">Starts From <span class="text-danger">*</span></label>
                <input type="date" class="form-control form-control-sm" formControlName="startsFrom"
                  [attr.disabled]="!canEditScheduler ? 'disabled' : null">
                <app-form-errors [control]="alertsServiceSchedularForm.controls.startsFrom"></app-form-errors>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Ends On <span class="text-danger">*</span></label>
                <input type="date" class="form-control form-control-sm" formControlName="endsOn"
                  [attr.disabled]="!canEditScheduler ? 'disabled' : null">
                <app-form-errors [control]="alertsServiceSchedularForm.controls.endsOn"></app-form-errors>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Daily Starts On <span class="text-danger">*</span></label>
                <input type="time" class="form-control form-control-sm" formControlName="dailyStartsOn"
                  [attr.disabled]="!canEditScheduler ? 'disabled' : null">
                <app-form-errors [control]="alertsServiceSchedularForm.controls.dailyStartsOn"></app-form-errors>
              </div>
              <div class="col-md-6">
                <label class="form-label fw-semibold">Daily Ends On <span class="text-danger">*</span></label>
                <input type="time" class="form-control form-control-sm" formControlName="dailyEndsOn"
                  [attr.disabled]="!canEditScheduler ? 'disabled' : null">
                <app-form-errors [control]="alertsServiceSchedularForm.controls.dailyEndsOn"></app-form-errors>
              </div>

              <div class="col-md-6">
                <label class="form-label">Last Execution</label>
                <input type="text" class="form-control form-control-sm" formControlName="lastExecutionTime" readonly>
              </div>
              <div class="col-md-6">
                <label class="form-label">Next Execution</label>
                <input type="text" class="form-control form-control-sm" formControlName="nextExecutionTime" readonly>
              </div>
            </div>

            <div class="d-flex justify-content-end gap-2 mt-4">
              <ng-container *ngIf="alertsServiceSchedularForm.get('schedularId')?.value && alertsServiceSchedularForm.get('schedularId')?.value !== 0 && !(isUniversalEditing && editingSection === 'scheduler')">
                <button class="btn btn-sm btn-outline-secondary" (click)="toggleSchedulerEdit()">
                  <i class="bi bi-pencil me-1"></i>
                  {{ alertsServiceSchedularForm.get('mappperId')?.value && alertsServiceSchedularForm.get('mappperId')?.value !== 0 ? 'Edit' : 'Add' }}
                </button>
              </ng-container>
              <ng-container *ngIf="isUniversalEditing && editingSection === 'scheduler'">
                <button class="btn btn-sm btn-outline-secondary" (click)="stopEditing()">Cancel</button>
                <button class="btn btn-sm btn-primary" (click)="saveSchedular()"
                  [disabled]="isLoadingSchedulerSave || alertsServiceSchedularForm.invalid">
                  <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoadingSchedulerSave"></span>Save
                </button>
              </ng-container>
            </div>
          </form>
        </div>
      </div>

      <!-- ── VARIABLES ─────────────────────────────────────────────── -->
      <div *ngIf="activeSection === 'variables'" class="section-card">
        <div class="section-card-header">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-braces text-primary fs-5"></i>
            <span class="fw-semibold">Variables</span>
          </div>
          <button class="btn btn-sm btn-outline-primary" (click)="onAddVariable()">
            <i class="bi bi-plus-circle me-1"></i>Add Variable
          </button>
        </div>
        <div class="section-card-body p-0">
          <!-- Header row -->
          <div class="row fw-semibold text-muted small border-bottom px-3 py-2 m-0" style="background: #f8f9fa;">
            <div class="col-md-3">Instance</div>
            <div class="col-md-4">Value</div>
            <div class="col-md-3">Type</div>
            <div class="col-md-2 text-end">Actions</div>
          </div>
          <div class="variable-scroll">
            <div *ngFor="let variable of serviceVariableList; let i = index"
              class="row align-items-center px-3 py-2 m-0 border-bottom" [id]="'var' + i">
              <div class="col-md-3">
                <input type="text" class="form-control form-control-sm" [(ngModel)]="variable.varInstance"
                  placeholder="Instance" [readonly]="!variable.isEditing">
              </div>
              <div class="col-md-4">
                <input type="text" class="form-control form-control-sm" [(ngModel)]="variable.varValue"
                  placeholder="Value" [readonly]="!variable.isEditing">
              </div>
              <div class="col-md-3">
                <select class="form-select form-select-sm" [(ngModel)]="variable.varType" [disabled]="!variable.isEditing">
                  <option [ngValue]="null" disabled>-- Type --</option>
                  <option *ngFor="let type of varTypeOptions" [ngValue]="type">{{ type }}</option>
                </select>
              </div>
              <div class="col-md-2 text-end">
                <button class="btn btn-sm btn-outline-primary me-1" (click)="onEditVariable(i)" title="{{ variable.isEditing ? 'Save' : 'Edit' }}">
                  <i class="bi" [ngClass]="variable.isEditing ? 'bi-check-lg' : 'bi-pencil'"></i>
                </button>
                <button class="btn btn-sm btn-outline-danger" (click)="onDeleteVariable(i)" title="Delete">
                  <i class="bi" [ngClass]="variable.variableId ? 'bi-trash' : 'bi-x-lg'"></i>
                </button>
              </div>
            </div>
            <div *ngIf="serviceVariableList.length === 0" class="text-center text-muted py-4 small">
              No variables defined. Click "Add Variable" to create one.
            </div>
          </div>
        </div>
      </div>

      <!-- ── ADVANCED ──────────────────────────────────────────────── -->
      <div *ngIf="activeSection === 'advanced'" class="section-card">
        <div class="section-card-header">
          <div class="d-flex align-items-center gap-2">
            <i class="bi bi-gear text-primary fs-5"></i>
            <span class="fw-semibold">Advanced</span>
          </div>
          <ng-container *ngIf="editingSection !== 'postService' && editingSection !== 'attachments'">
            <button class="btn btn-sm btn-outline-secondary" (click)="togglePostServiceEdit()"
              [disabled]="isUniversalEditing">
              <i class="bi bi-pencil me-1"></i>Edit
            </button>
          </ng-container>
          <div class="d-flex gap-2" *ngIf="editingSection === 'postService' || editingSection === 'attachments'">
            <button class="btn btn-sm btn-outline-secondary" (click)="stopEditing()">Cancel</button>
            <button class="btn btn-sm btn-primary" (click)="updateAlertsServiceMaster()" [disabled]="isLoadingServiceUpdate">
              <span class="spinner-border spinner-border-sm me-1" *ngIf="isLoadingServiceUpdate"></span>Save
            </button>
          </div>
        </div>
        <div class="section-card-body">
          <form [formGroup]="alertServiceForm">

            <!-- Post Service option card -->
            <div class="option-card mb-3" [class.option-card--active]="alertServiceForm.get('showPostForm')?.value">
              <div class="option-card-header">
                <div class="d-flex align-items-center gap-2">
                  <i class="fa-solid fa-gears text-primary fs-5"></i>
                  <div>
                    <div class="fw-semibold">Post Service</div>
                    <small class="text-muted">Run a query after each notification is sent</small>
                  </div>
                </div>
                <div class="form-check form-switch mb-0">
                  <input class="form-check-input" type="checkbox" role="switch" formControlName="showPostForm">
                </div>
              </div>
              <div class="option-card-body" *ngIf="alertServiceForm.get('showPostForm')?.value">
                <div class="row g-3">
                  <div class="col-md-5">
                    <label class="form-label">Post Send Data Source Type</label>
                    <app-valuelist-selector [control]="alertServiceForm.controls.postSendDataSourceType"
                      [vlCode]="'ANNPSDST'" [placeholder]="'-- Select Type --'">
                    </app-valuelist-selector>
                  </div>
                  <div class="col-12">
                    <label class="form-label">Post Send Query</label>
                    <textarea class="form-control form-control-sm font-monospace" formControlName="postSendDataSourceDef"
                      rows="5" placeholder="UPDATE ... SET NStatus = 'Sent' WHERE Id = {{'{{'}}MasterId{{'}}'}}"></textarea>
                  </div>
                </div>
              </div>
            </div>

            <!-- Attachment option card -->
            <div class="option-card"
              *ngIf="!['WhatsApp','SMS','PowerShellScript','Others'].includes(alertServiceForm.get('alertType')?.value || '')"
              [class.option-card--active]="alertServiceForm.get('hasAttachment')?.value">
              <div class="option-card-header">
                <div class="d-flex align-items-center gap-2">
                  <i class="bi bi-paperclip text-primary fs-5"></i>
                  <div>
                    <div class="fw-semibold">Attachment</div>
                    <small class="text-muted">Attach a generated file to the notification</small>
                  </div>
                </div>
                <div class="form-check form-switch mb-0">
                  <input class="form-check-input" type="checkbox" role="switch" formControlName="hasAttachment">
                </div>
              </div>
              <div class="option-card-body" *ngIf="alertServiceForm.get('hasAttachment')?.value">
                <div class="row g-3">
                  <div class="col-md-4">
                    <label class="form-label">Attachment Type</label>
                    <app-valuelist-selector [control]="alertServiceForm.controls.attachmentType"
                      [vlCode]="'ANNAT'" [placeholder]="'-- Select Type --'">
                    </app-valuelist-selector>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Attachment File Type</label>
                    <app-valuelist-selector [control]="alertServiceForm.controls.attachmentFileType"
                      [vlCode]="'ANNAFT'" [placeholder]="'-- Select File Type --'">
                    </app-valuelist-selector>
                  </div>
                  <div class="col-md-4">
                    <label class="form-label">Output File Name</label>
                    <input type="text" class="form-control form-control-sm" formControlName="outputFileName"
                      placeholder="report_{{'{{'}}Date{{'}}'}}.pdf">
                  </div>
                  <div class="col-12">
                    <label class="form-label">Attachment Path</label>
                    <input class="form-control form-control-sm" type="text" formControlName="attachmentPath"
                      placeholder="Path to attachment template">
                  </div>
                </div>
              </div>
            </div>

          </form>
        </div>
      </div>

    </div><!-- /content panel -->
  </div><!-- /two-column -->

</ng-container>
